import { Either, left, right } from 'fp-ts/lib/Either'
import { fromNullable } from 'fp-ts/lib/Option'

import * as ImageConfig from '@/config/assets/image'
import { ImageAppKind } from '@/models/api/image'
import { Price } from '@/models/api/price/price'
import { ApiProduct } from '@/models/api/product'
import { ApiProductclass } from '@/models/api/productclass'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { SkuPrice, SkuPriceList, SkuWithPrice } from '@/models/item/sku'
import * as DomainSKUFactory from '@/models/item/sku/factory/sku'
import CanRequestRestockSpec from '@/models/item/sku/sale-status/specification/each/can-request-restock-spec'
import { BundleDiscount } from '@/services/api/bundle-discount'
import GetStockStatus from '@/usecase/get-stock-status'
import { DateFormat } from '@/util/format'
import Util, { CartInfo } from '@ajax/modules/util'

/**
 * FKUを作成する際のコンテキスト
 * 特定のFKUのみに関連する情報でなく、ROOT全体に関連する情報をひとまとめで持つ
 */
export interface CreateFkuContext {
  skuPriceList: SkuPriceList
  bundleDiscountList: BundleDiscount[]
}

/**
 * 商品詳細UI向けに、SKUが満たす販売・通知条件を優先順位付きで1つに解決した状態。
 * Engineのsales_statusそのものではない。
 */
export type SkuResolvedSalesStatus = 'standard-order'
  | 'pre-order'
  | 'restock-request'
  | 'new-arrival-request'
  | 'coming-soon'
  | 'sold-out'
  | 'none'

export function createFku(raw: ApiProductclass, context: CreateFkuContext): Either<Error, Fku> {
  try {
    if (raw.parts.length === 0) {
      throw new Error(`Could not create fku because parts is empty. fku id is ${raw.id}`)
    }

    const brandEnglishName = raw.belongingBrand.englishName
    const behavior = BehaviorConfig.createFromBrand(brandEnglishName)
    const foundBundleDiscount = context.bundleDiscountList.find(
      bundleDiscount => bundleDiscount.fku_ids.includes(raw.id)
    ) || null

    const defaultDisplayLevel = 9999
    const skuList = raw.parts.sort(
      (a, b) => a.getSizeDisplayLevelOrElse(defaultDisplayLevel) - b.getSizeDisplayLevelOrElse(defaultDisplayLevel)
    ).map((p) => {
      const skuPrice = context.skuPriceList.findBySkuId(p.id)
      if (!skuPrice) {
        throw new Error(`Could not fetch sku price for ${p.id}`)
      }
      const skuWithPrice = DomainSKUFactory.createWithPrice(p, skuPrice)
      const getStockStatus = GetStockStatus.createViaConfig(behavior)
      const canRequestRestockSpec = new CanRequestRestockSpec(behavior)
      const cartInfo = Util.getCartInfoForApiProduct(p)

      return new Sku(p, skuWithPrice, getStockStatus, canRequestRestockSpec, cartInfo)
    })

    return right(new Fku(raw, foundBundleDiscount, skuList))
  } catch (error) {
    return left(error as Error)
  }
}

export class Fku {
  constructor(
    private readonly raw: ApiProductclass,
    public readonly bundleDiscount: BundleDiscount | null,
    public readonly skuList: Sku[]
  ) {}

  get id(): string {
    return this.raw.id
  }

  get colorLabel(): string {
    return fromNullable(this.raw.color).map(tag => tag.label).getOrElse('-')
  }

  /**
   * カラー選択で利用するサムネイル画像のURLを返す。
   * ブランド運営者が代表画像とは異なる画像をサムネイル画像に指定できるようにするため、
   * レンズ画像が存在すればそれを優先的に採用する。
   */
  get thumbnailImageUrl(): string {
    const lensImage = this.raw.images
      .filter(image => image.imageAppKind === ImageAppKind.Lens)
      .sort((a, b) => a.displayLevel - b.displayLevel)[0]
    const thumbnailImage = lensImage || this.raw.representativeImage

    return thumbnailImage ? thumbnailImage.thumbnailUrl : ImageConfig.dummyImagePath
  }

  get representativeImageId(): string | null {
    const representativeImage = this.raw.representativeImage

    return representativeImage ? representativeImage.id : null
  }

  get hasDiscountSaleSku(): boolean {
    return this.skuList.some(s => s.isOnDiscountSale)
  }
}

export class Sku {
  constructor(
    private readonly raw: ApiProduct,
    private readonly domainSkuWithPrice: SkuWithPrice,
    private readonly getStockStatus: GetStockStatus,
    private readonly canRequestRestockSpec: CanRequestRestockSpec,
    private readonly cartInfo: CartInfo
  ) {}

  get id(): string {
    return this.raw.id
  }

  get shortId(): string {
    return this.raw.shortId
  }

  get skuNumber(): string {
    return this.raw.skuNumber
  }

  get sizePathname(): string | null {
    return this.raw.getSizeTagPathname()
  }

  get sizeLabel(): string {
    return this.raw.getSizeLabelOrElse('-')
  }

  get stockText(): string {
    return this.getStockStatus.toText(this.domainSkuWithPrice.sku.stock)
  }

  get price(): SkuPrice {
    return this.domainSkuWithPrice.price
  }

  get formattedPrice(): string {
    return Util.formatJPYPrice(this.domainSkuWithPrice.price.prices.selected.price)
  }

  get deliveryStartAt(): string {
    return this.cartInfo.presales && this.raw.regularSaleStartAt
      ? DateFormat.partOfMonth(this.raw.regularSaleStartAt)
      : ''
  }

  /**
   * １回で選択できる上限数。
   * SKUの購入上限数、在庫数、有効価格で追加可能な残数の最も小さい値を返す。
   */
  get maxSelectableCount(): number {
    return Math.min(
      this.raw.maxSellCount,
      this.stockAmount,
      this.price.prices.selected.rest_amount.add_to_cart
    )
  }

  resolveSalesStatus(current: Date): SkuResolvedSalesStatus {
    if (this.stockExists && this.isWithinSalesPeriod(current)) {
      return 'standard-order'
    }

    if (this.isPreSales) {
      return 'pre-order'
    }

    if (this.isRestockRequest) {
      return 'restock-request'
    }

    if (this.isNewArrivalRequest) {
      return 'new-arrival-request'
    }

    if (this.isWait) {
      return 'coming-soon'
    }

    if (this.isSoldOut || this.isAfterSalesPeriod(current)) {
      return 'sold-out'
    }

    return 'none'
  }

  get isOnDiscountSale(): boolean {
    return this.raw.isOnDiscountSale
  }

  get discountRate(): number {
    return this.priceForDiscountDisplay.rateOfDiscount
  }

  get discountAmount(): number {
    return this.priceForDiscountDisplay.regular - this.priceForDiscountDisplay.discounted
  }

  private get stockAmount(): number {
    return this.getStockStatus.amount(this.domainSkuWithPrice.sku.stock)
  }

  private get stockExists(): boolean {
    return this.getStockStatus.exists(this.domainSkuWithPrice.sku.stock)
  }

  private get isSoldOut(): boolean {
    return this.getStockStatus.isSoldOut(this.domainSkuWithPrice.sku.stock)
  }

  private get isWait(): boolean {
    return this.cartInfo.wait
  }

  private get isNewArrivalRequest(): boolean {
    return this.cartInfo.newitem
  }

  private get isRestockRequest(): boolean {
    return this.canRequestRestockSpec.verify(this.raw)
  }

  private get isPreSales(): boolean {
    return this.cartInfo.presales
  }

  /**
   * これは有料会員価格を考慮しないことに注意
   * 割引表示にのみ利用するが、有料会員価格とセールは併用がないため動作に問題はない
   * 本当は this.price (SkuPrice) に割引前価格の情報を入れるなどの整理をしたい
   */
  private get priceForDiscountDisplay(): Price {
    return this.raw.price
  }

  private isWithinSalesPeriod(current: Date): boolean {
    return this.domainSkuWithPrice.sku.salePeriod.include(current)
  }

  private isAfterSalesPeriod(current: Date): boolean {
    return this.raw.isAfterRegularSaleEnd(current)
  }
}
